import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box } from '@mantine/core';
import { useForm } from '@mantine/form';
import i18n from 'i18next';

import { EncodedHeader, Footer, Section, Stepper } from 'components';
import { AppConstants, ServiceConstants } from '../../../constants';
import PersonalInformationStep from './personal-information';
import { CardOptionsResponse, CardResponse, ICardRequestUpdate } from 'types';
import { CardService, PublicService } from 'services';
import { IRootState } from 'config/redux';
import { AppUtils } from 'utils';
import FamilyInformationStep from './family-information';
import WorkInformationStep from './work-information';
import CardInformationStep from './card-information';
import CreditCardRequestExistsPage from '../exists';
import { useSearchParams } from 'react-router-dom';
import CreditCardRequestSubmittedPage from './submitted';

const {
  CREDIT_CARD_REQUEST_FORM_STEPS,
  CREDIT_CARD_REQUEST_STEP_PERSONAL,
  CREDIT_CARD_REQUEST_STEP_FAMILY,
  CREDIT_CARD_REQUEST_STEP_CARD,
  PHONE_NUMBER_CODES,
} = AppConstants;

const {
  REQUEST_CARD_CREATE_ID,
  REQUEST_CARD_REQUEST_ID,
  REQUEST_CARD_OPTIONS_ID,
  REQUEST_CARD_UNREQUESTED_ID,
  REQUEST_CARD_MULTIPLE_UPDATE_ID,
  REQUEST_CARD_SUBMIT_ID,
  REQUEST_LOGIN_ID,
} = ServiceConstants;

const CreditCardRequestFormPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const cardOptionsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_OPTIONS_ID]);
  const loginServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_LOGIN_ID]);
  const unrequestedServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_UNREQUESTED_ID]);
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_CREATE_ID]);
  const getServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_REQUEST_ID]);
  const updateFieldsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_MULTIPLE_UPDATE_ID]);
  const submitServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARD_SUBMIT_ID]);

  const [options, setOptions] = useState<CardOptionsResponse | undefined>({} as CardOptionsResponse);
  const [activeStep, setActiveStep] = useState<number>(CREDIT_CARD_REQUEST_STEP_PERSONAL);
  const [requestExists, setRequestExists] = useState<boolean>(false);
  const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false);

  const timeouts = useRef<any>({});
  const shouldUpdateFields = useRef<boolean>(false);

  const form = useForm({
    initialValues: {} as CardResponse,
    onValuesChange: (values, previousValues) => {
      const changedFields = AppUtils.getChangedFieldsForCard(values, previousValues);

      if (!AppUtils.isEmpty(changedFields) && shouldUpdateFields.current) {
        scheduleFieldUpdates(values.identification, changedFields);
      }
    }
  });

  useEffect(() => {
    CardService.cardOptions();
    if (!AppUtils.isEmpty(cid)) {
      CardService.getRequest(cid!);
    }
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(cardOptionsServiceObject, REQUEST_CARD_OPTIONS_ID, setOptions);
  }, [cardOptionsServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      getServiceObject,
      REQUEST_CARD_REQUEST_ID,
      (val) => {
        if (val.requestedAt) {
          setRequestSubmitted(true);
          setActiveStep(CREDIT_CARD_REQUEST_STEP_CARD);
        } else {
          setValues(val);
          shouldUpdateFields.current = true;
          setActiveStep(CREDIT_CARD_REQUEST_STEP_FAMILY);
        }
      }
    );
  }, [getServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      loginServiceObject,
      REQUEST_LOGIN_ID,
      () => {
        CardService.getUnrequested();
      },
      () => form.setFieldError('phoneNumber', t('login.error.credentials'))
    );
  }, [loginServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      unrequestedServiceObject,
      REQUEST_CARD_UNREQUESTED_ID,
      (val) => {
        if (val) {
          setRequestExists(true);
        } else {
          CardService.createRequest(form.values);
        }
      }
    );
  }, [unrequestedServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_CARD_CREATE_ID,
      (val) => {
        const url = new URL(window.location.href);
        url.searchParams.set('cid', val.identification);
        window.history.pushState(null, '', url.toString());
        setValues(val);
        shouldUpdateFields.current = true;
        setActiveStep(CREDIT_CARD_REQUEST_STEP_FAMILY);
      }
    );
  }, [createServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(submitServiceObject, REQUEST_CARD_CREATE_ID,
      () => setRequestSubmitted(true),
    );
  }, [submitServiceObject]);

  const setValues = useCallback((val: CardResponse | Partial<CardResponse>) => {
    const updates: Partial<CardResponse> = { ...val };

    if ('birthDate' in val) {
      updates.birthDate = val.birthDate ? new Date(val.birthDate) : undefined;
    }

    if ('livingFrom' in val) {
      updates.livingFrom = val.livingFrom ? new Date(val.livingFrom) : undefined;
    }

    if ('companyEmployedAt' in val) {
      updates.companyEmployedAt = val.companyEmployedAt ? new Date(val.companyEmployedAt) : undefined;
    }

    form.setValues(updates);
  }, []);

  const checkApplication = useCallback(() => {
    const languageFromI18n = i18n.language.toUpperCase() as 'EN' | 'DE' | 'SR' | 'AL' | 'ES' | 'FR' | 'IT' | 'PT' | 'TR';
    const countryCode = PHONE_NUMBER_CODES.find(code => form.values.phoneNumber.startsWith(code));

    PublicService.login({
      email: form.values.email,
      countryCode: countryCode!,
      phoneNumber: form.values.phoneNumber.slice(countryCode!.length),
      firstName: form.values.firstName!,
      lastName: form.values.lastName!,
      language: languageFromI18n,
    });
  }, [form, i18n]);

  const submitRequest = useCallback(() => {
    CardService.submitRequest(form.values.identification);
  }, [form]);

  function scheduleFieldUpdates(
    identification: string,
    changedFields: ICardRequestUpdate[],
  ) {
    if (changedFields.length === 1) {
      const obj = changedFields[0];
      clearTimeout(timeouts.current[obj.name]);
      timeouts.current[obj.name] = setTimeout(() => {
        CardService.updateRequestMultiple(identification, [obj]);
      }, 500);
    } else {
      CardService.updateRequestMultiple(identification, changedFields);
    }
  }

  useEffect(() => {
    AppUtils.resolveServiceObject(updateFieldsServiceObject, REQUEST_CARD_MULTIPLE_UPDATE_ID,
      (val) => {
        setValues(toObject(val));
      }
    );
  }, [updateFieldsServiceObject]);

  function toObject(entries: ICardRequestUpdate[]): Partial<CardResponse> {
    return entries.reduce((acc, { name, value }) => {
      acc[name] = value;
      return acc;
    }, {} as Partial<CardResponse>);
  }

  if (AppUtils.isEmpty(options)) {
    return <></>
  }

  return (
    <>
      <Section center={true}>
        <Box w={'100%'}>
          <EncodedHeader t={t} />
          <Stepper
            numOfSteps={CREDIT_CARD_REQUEST_FORM_STEPS}
            setActiveStep={setActiveStep}
            allowedReturnToStart={false}
            titleKey={'card.step'}
            t={t}
            activeStep={activeStep}
            steps={[
              requestExists ? <CreditCardRequestExistsPage /> : <PersonalInformationStep form={form} options={options!} onSubmit={checkApplication} t={t} />,
              <FamilyInformationStep form={form} options={options!} onSubmit={() => (setActiveStep(old => old + 1))} t={t} />,
              <WorkInformationStep form={form} options={options!} onSubmit={() => (setActiveStep(old => old + 1))} t={t} />,
              requestSubmitted ? <CreditCardRequestSubmittedPage /> : <CardInformationStep form={form} options={options!} onSubmit={submitRequest} t={t} />
            ]}
          />
        </Box>
      </Section>
      <Footer />
    </>
  )
}

export default CreditCardRequestFormPage;